import { useState } from 'react'
import { Input } from './Input'
import { Button } from './Button'
import { Alert } from './Alert'
import { Client } from '../services/clientService'
import { useMatterService, Matter, Task, MatterType } from '../services/matterService'
import { useTerminology } from '../contexts/TerminologyContext'

interface CreateMatterFormProps {
  clients: Client[]
  onCreated: (result: { matter: Matter; tasks: Task[] }) => void
}

const matterTypes: MatterType[] = ['Sale', 'Purchase', 'Transfer', 'Subdivision']

const streetNames = ['High St', 'Chapel St', 'Church St', 'Station Rd', 'Victoria Ave', 'Beach Rd', 'Park St', 'King St']
const suburbs = ['Melbourne', 'Richmond', 'St Kilda', 'Fitzroy', 'Brunswick', 'South Yarra', 'Carlton', 'Prahran']

const randomFrom = (values: string[]) => values[Math.floor(Math.random() * values.length)]

const randomAddress = () => `${Math.floor(Math.random() * 200) + 1} ${randomFrom(streetNames)}, ${randomFrom(suburbs)} VIC`

const defaultSettlementDate = () => {
  const d = new Date()
  d.setDate(d.getDate() + 60)
  return d.toISOString().slice(0, 10)
}

const clientLabel = (c: Client) => c.fullName || `${c.firstName} ${c.lastName}`

export const CreateMatterForm: React.FC<CreateMatterFormProps> = ({ clients, onCreated }) => {
  const terminology = useTerminology()
  const [matterType, setMatterType] = useState<MatterType>('Sale')
  const [primaryClientId, setPrimaryClientId] = useState('')
  const [secondaryClientId, setSecondaryClientId] = useState('')
  const [propertyAddress, setPropertyAddress] = useState(randomAddress)
  const [settlementDate, setSettlementDate] = useState(defaultSettlementDate)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { createMatter } = useMatterService()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!primaryClientId) {
      setError(`Select a primary ${terminology.client.toLowerCase()}`)
      return
    }

    setLoading(true)
    try {
      const result = await createMatter({
        matterType,
        primaryClientId,
        secondaryClientId: secondaryClientId || undefined,
        propertyAddress,
        settlementDate,
      })
      onCreated(result)
      setPropertyAddress(randomAddress())
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to create ${terminology.matter.toLowerCase()}`)
    } finally {
      setLoading(false)
    }
  }

  if (clients.length === 0) {
    return <p className="text-sm text-gray-600">Add a {terminology.client.toLowerCase()} first before opening a {terminology.matter.toLowerCase()}.</p>
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <Alert type="error">{error}</Alert>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="w-full">
          <label className="block text-sm font-medium text-gray-700 mb-1">{terminology.matter} Type</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
            value={matterType}
            onChange={(e) => setMatterType(e.target.value as MatterType)}
          >
            {matterTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <Input label="Settlement Date" type="date" value={settlementDate} onChange={(e) => setSettlementDate(e.target.value)} />

        <div className="w-full">
          <label className="block text-sm font-medium text-gray-700 mb-1">Primary {terminology.client}</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
            value={primaryClientId}
            onChange={(e) => setPrimaryClientId(e.target.value)}
          >
            <option value="">Select a {terminology.client.toLowerCase()}</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{clientLabel(c)}</option>)}
          </select>
        </div>

        <div className="w-full">
          <label className="block text-sm font-medium text-gray-700 mb-1">Secondary {terminology.client} (optional)</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
            value={secondaryClientId}
            onChange={(e) => setSecondaryClientId(e.target.value)}
          >
            <option value="">None</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{clientLabel(c)}</option>)}
          </select>
        </div>
      </div>

      <Input label="Property Address" value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} />

      <Button type="submit" disabled={loading}>
        {loading ? `Creating ${terminology.matter.toLowerCase()}...` : `Create ${terminology.matter}`}
      </Button>
    </form>
  )
}
